import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, ClipboardList, Pencil, Trash2, Building2, Truck,
  CheckCircle2, AlertCircle, Calendar, FileText, MapPin,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { can } from '@/types';
import {
  useOrder, useUpdateOrderStatus, useDeleteOrder,
} from '@/hooks/useSales';
import {
  LINE_ITEM_KIND_LABELS, ORDER_PROGRESSION, ORDER_STATUS_LABELS,
  ORDER_STATUSES, type LineItem, type OrderStatus,
} from '@/types/sales';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { LoaderScreen } from '@/components/ui/LoaderScreen';
import { EmptyState } from '@/components/ui/EmptyState';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { OrderStatusBadge } from '@/components/quotes/StatusBadges';
import { OrderFormModal } from '@/components/orders/OrderFormModal';
import { TotalsPanel } from '@/components/quotes/TotalsPanel';
import { formatCurrency } from '@/lib/utils';

export function OrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = user ? can(user.role, 'orders', 'write') : false;
  const showCost = user ? can(user.role, 'financials', 'read') : false;

  const { data: order, isLoading, error } = useOrder(id);
  const updateStatus = useUpdateOrderStatus();
  const deleteOrder = useDeleteOrder();

  const [editOpen, setEditOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (isLoading) return <LoaderScreen />;
  if (error || !order) {
    return (
      <Card>
        <EmptyState
          icon={AlertCircle}
          title="Could not load order"
          description={(error as { message?: string })?.message ?? 'Order not found.'}
          action={<Link to="/orders"><Button variant="secondary" leftIcon={<ArrowLeft size={14} />}>Back to Orders</Button></Link>}
        />
      </Card>
    );
  }

  async function setStatus(s: OrderStatus) {
    if (!order || s === order.status) return;
    try {
      await updateStatus.mutateAsync({ id: order.id, status: s });
      toast.success(`Status → ${ORDER_STATUS_LABELS[s]}`);
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Status update failed.');
    }
  }

  async function onDelete() {
    if (!order) return;
    try {
      await deleteOrder.mutateAsync(order.id);
      toast.success(`Deleted ${order.number}`);
      navigate('/orders');
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Delete failed.');
    }
  }

  const isLate = order.promisedDate && new Date(order.promisedDate).getTime() < Date.now()
    && !['shipped', 'delivered', 'cancelled'].includes(order.status);

  return (
    <div className="space-y-8">
      <Link to="/orders" className="inline-flex items-center gap-2 text-xs text-text-dim hover:text-text-muted transition-colors">
        <ArrowLeft size={12} />
        <span className="kicker">All Orders</span>
      </Link>

      {/* Hero */}
      <header className="flex items-start justify-between gap-6">
        <div className="flex items-start gap-5 min-w-0 flex-1">
          <div className="shrink-0 h-16 w-16 rounded-sm bg-walnut-700 border border-brass-400/30 flex items-center justify-center">
            <ClipboardList size={22} className="text-brass-300" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <span className="kicker">Work Order</span>
              <OrderStatusBadge status={order.status} />
              {isLate && <span className="kicker text-danger">Past Due</span>}
            </div>
            <h1 className="font-display text-4xl text-text leading-tight tracking-tight font-mono">
              {order.number}
            </h1>
            {order.projectName && (
              <p className="text-text-muted mt-2 text-sm">{order.projectName}</p>
            )}
            {order.sourceQuoteId && (
              <p className="text-2xs text-text-dim mt-1.5">
                Converted from <Link to={`/quotes/${order.sourceQuoteId}`} className="text-brass-300 hover:text-brass-200 font-mono">quote ↗</Link>
              </p>
            )}
          </div>
        </div>

        {canWrite && (
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="secondary" leftIcon={<Pencil size={13} />} onClick={() => setEditOpen(true)}>
              Edit
            </Button>
            <Button variant="ghost" onClick={() => setConfirmDelete(true)} aria-label="Delete">
              <Trash2 size={14} />
            </Button>
          </div>
        )}
      </header>

      {/* Progression bar */}
      <Card>
        <div className="flex items-end justify-between gap-4 mb-5">
          <div>
            <div className="kicker mb-1">Production Progress</div>
            <div className="text-sm text-text">Track this order through the floor.</div>
          </div>
          {canWrite && (
            <div className="w-56">
              <Select
                value={order.status}
                onChange={(e) => setStatus(e.target.value as OrderStatus)}
                options={ORDER_STATUSES.map((s) => ({ value: s, label: ORDER_STATUS_LABELS[s] }))}
              />
            </div>
          )}
        </div>

        <ProgressionBar status={order.status} milestones={order.milestones} />
      </Card>

      {/* Meta strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border rounded-sm overflow-hidden border border-border">
        <Meta label="Client" value={
          <Link to={`/clients/${order.clientId}`} className="text-text hover:text-brass-300 inline-flex items-center gap-1.5">
            <Building2 size={12} />
            {order.clientName ?? order.clientId}
          </Link>
        } />
        <Meta label="Received" value={order.receivedDate ? new Date(order.receivedDate).toLocaleDateString('en-US', { dateStyle: 'medium' }) : '—'} />
        <Meta
          label="Promised"
          value={order.promisedDate ? new Date(order.promisedDate).toLocaleDateString('en-US', { dateStyle: 'medium' }) : '—'}
          alert={!!isLate}
        />
        <Meta label="Total" value={formatCurrency(order.totals?.total ?? 0)} accent />
      </div>

      {/* Two-column body */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
        {/* Line items */}
        <Card className="p-0 overflow-hidden">
          <header className="px-6 py-4 border-b border-border">
            <div className="kicker mb-1">Line Items · {order.lineItems.length}</div>
            <h3 className="font-display text-lg text-text">Specifications</h3>
          </header>

          {order.lineItems.length === 0 ? (
            <EmptyState icon={FileText} title="No line items" description="Edit this order to add specifications." />
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="kicker px-6 py-3 font-normal w-12">#</th>
                  <th className="kicker px-6 py-3 font-normal">Item</th>
                  <th className="kicker px-6 py-3 font-normal text-right">Qty</th>
                  <th className="kicker px-6 py-3 font-normal text-right">Unit Price</th>
                  <th className="kicker px-6 py-3 font-normal text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {order.lineItems.map((it, idx) => (
                  <LineItemReadRow key={it.id} item={it} index={idx} />
                ))}
              </tbody>
            </table>
          )}
        </Card>

        {/* Sidebar */}
        <div className="space-y-4">
          <TotalsPanel
            lineItems={order.lineItems}
            discountPct={order.discountPct}
            taxRatePct={order.taxRatePct}
            shipping={order.shipping}
            showMargin={showCost}
            readonly
          />

          {/* Shipping address */}
          {order.shippingAddress && hasAddr(order.shippingAddress) && (
            <Card>
              <div className="kicker mb-3 flex items-center gap-2">
                <Truck size={11} /> Ship To
              </div>
              <address className="text-sm text-text not-italic leading-relaxed">
                {order.shippingAddress.street1 && <div>{order.shippingAddress.street1}</div>}
                {order.shippingAddress.street2 && <div>{order.shippingAddress.street2}</div>}
                {(order.shippingAddress.city || order.shippingAddress.state || order.shippingAddress.postalCode) && (
                  <div>
                    {[order.shippingAddress.city, order.shippingAddress.state, order.shippingAddress.postalCode]
                      .filter(Boolean).join(', ')}
                  </div>
                )}
                {order.shippingAddress.country && <div className="text-text-muted text-xs mt-1">{order.shippingAddress.country}</div>}
              </address>
              {order.shippingAddress.contactName && (
                <div className="mt-3 pt-3 border-t border-border text-xs">
                  <div className="kicker mb-1">Site Contact</div>
                  <div className="text-text">{order.shippingAddress.contactName}</div>
                  {order.shippingAddress.contactPhone && (
                    <div className="text-text-muted tabular">{order.shippingAddress.contactPhone}</div>
                  )}
                </div>
              )}
            </Card>
          )}

          {order.poNumber && (
            <Card>
              <div className="kicker mb-2">Client PO</div>
              <div className="font-mono text-text">{order.poNumber}</div>
            </Card>
          )}

          {order.notes && (
            <Card>
              <div className="kicker mb-2">Notes</div>
              <p className="text-sm text-text-muted whitespace-pre-wrap leading-relaxed">{order.notes}</p>
            </Card>
          )}

          {order.internalNotes && canWrite && (
            <Card className="border-warning/30">
              <div className="kicker text-warning mb-2">Internal Only</div>
              <p className="text-sm text-text-muted whitespace-pre-wrap leading-relaxed">{order.internalNotes}</p>
            </Card>
          )}
        </div>
      </div>

      <OrderFormModal open={editOpen} onClose={() => setEditOpen(false)} order={order} />
      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={onDelete}
        loading={deleteOrder.isPending}
        destructive
        title={`Delete ${order.number}?`}
        confirmLabel="Delete order"
        description="This permanently removes the order. Cannot be undone."
      />
    </div>
  );
}

function ProgressionBar({ status, milestones }: { status: OrderStatus; milestones?: { engineeredAt?: string; scheduledAt?: string; productionStartedAt?: string; qcAt?: string; shippedAt?: string; deliveredAt?: string } }) {
  const cancelled = status === 'cancelled';
  const onHold = status === 'on_hold';

  if (cancelled) {
    return (
      <div className="p-4 bg-danger/5 border border-danger/30 rounded-sm flex items-center gap-3 text-sm text-danger">
        <AlertCircle size={14} />
        This order has been cancelled. No production activity will occur.
      </div>
    );
  }

  if (onHold) {
    return (
      <div className="p-4 bg-warning/5 border border-warning/30 rounded-sm flex items-center gap-3 text-sm text-warning">
        <AlertCircle size={14} />
        This order is on hold. Resume by changing status above.
      </div>
    );
  }

  const currentIdx = ORDER_PROGRESSION.indexOf(status);
  const milestoneMap: Record<OrderStatus, string | undefined> = {
    received:      undefined,
    engineered:    milestones?.engineeredAt,
    scheduled:     milestones?.scheduledAt,
    in_production: milestones?.productionStartedAt,
    qc:            milestones?.qcAt,
    shipped:       milestones?.shippedAt,
    delivered:     milestones?.deliveredAt,
    on_hold:       undefined,
    cancelled:     undefined,
  };

  return (
    <div className="flex items-start gap-px">
      {ORDER_PROGRESSION.map((s, i) => {
        const reached = i <= currentIdx;
        const isCurrent = s === status;
        const ts = milestoneMap[s];
        return (
          <div key={s} className="flex-1">
            <div className={`h-1.5 transition-colors ${reached ? (isCurrent ? 'bg-brass-300' : 'bg-brass-400') : 'bg-border'}`} />
            <div className="pt-3">
              <div className="flex items-center gap-1.5">
                {reached && <CheckCircle2 size={10} className="text-brass-300" />}
                <div className={`text-2xs uppercase tracking-ultra ${reached ? 'text-text-muted' : 'text-text-dim'}`}>
                  {ORDER_STATUS_LABELS[s]}
                </div>
              </div>
              {ts && (
                <div className="text-2xs text-text-dim tabular mt-0.5">
                  {new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Meta({ label, value, accent, alert }: { label: string; value: React.ReactNode; accent?: boolean; alert?: boolean }) {
  return (
    <div className="bg-bg-panel p-4">
      <div className="kicker mb-1.5">{label}</div>
      <div className={`text-sm tabular ${accent ? 'font-display text-2xl text-brass-300' : alert ? 'text-danger' : 'text-text'}`}>
        {value}
      </div>
    </div>
  );
}

function LineItemReadRow({ item, index }: { item: LineItem; index: number }) {
  return (
    <tr className="border-b border-border last:border-0 hover:bg-bg-hover/30 transition-colors">
      <td className="px-6 py-4 align-top text-text-dim tabular">{String(index + 1).padStart(2, '0')}</td>
      <td className="px-6 py-4">
        <div className="flex items-baseline gap-2 mb-0.5">
          <span className="kicker text-brass-300">{LINE_ITEM_KIND_LABELS[item.kind]}</span>
          {item.sku && <span className="text-2xs font-mono text-text-dim">#{item.sku}</span>}
        </div>
        <div className="text-text">{item.description}</div>
        {(item.veneerSpecies || item.finish || item.dimensions) && (
          <div className="text-xs text-text-dim mt-1 flex items-center gap-3 flex-wrap">
            {item.veneerSpecies && <span>{item.veneerSpecies}</span>}
            {item.finish && <span>· {item.finish}</span>}
            {item.dimensions && <span>· {item.dimensions}</span>}
          </div>
        )}
      </td>
      <td className="px-6 py-4 text-right tabular text-text whitespace-nowrap">
        {item.quantity} {item.unit ?? 'ea'}
      </td>
      <td className="px-6 py-4 text-right tabular text-text-muted">
        {formatCurrency(item.unitPrice)}
      </td>
      <td className="px-6 py-4 text-right tabular text-text font-medium">
        {formatCurrency(item.quantity * item.unitPrice)}
      </td>
    </tr>
  );
}

function hasAddr(a: { street1?: string; city?: string; state?: string }) {
  return !!(a.street1 || a.city || a.state);
}

// Suppress unused import warning
void MapPin; void Calendar;
